import {
  ArrowOutwardIconData,
  BrushIconData,
  CategoryIconData,
  CircleIconData,
  Crop169IconData,
  GridOffIconData,
  GridOnIconData,
  StickyNote2IconData,
  TitleIconData,
} from "@meiden-connect/material-symbols";
import { BinarySwitch, Button, Icon } from "@meiden-connect/ui";
import type React from "react";
import { useCanvasStore, type CanvasTool, type ShapeKind } from "../store/canvas-store";
import {
  PEN_COLOR_HEX,
  savePenColor,
  type PenColor,
} from "../../../lib/pen-preference";
import {
  saveStickyColor,
  STICKY_COLOR_HEX,
  type StickyColor,
} from "../../../lib/sticky-preference";

// 下部中央のフローティングツールバー。
// 図形・ペン・付箋はサブ選択パネルで種類や色を選択できる。
// すべてアイコンボタンに統一し、ホバー時の title でラベルを案内する。
export function ToolBar(): JSX.Element {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const setShapeKind = useCanvasStore((s) => s.setShapeKind);
  const penColor = useCanvasStore((s) => s.penColor);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);
  const gridSnapEnabled = useCanvasStore((s) => s.gridSnapEnabled);
  const setGridSnapEnabled = useCanvasStore((s) => s.setGridSnapEnabled);

  const buttons: { tool: CanvasTool; label: string; icon: string }[] = [
    { tool: "place-sticky", label: "付箋を配置", icon: StickyNote2IconData },
    { tool: "place-shape", label: "図形を配置", icon: CategoryIconData },
    { tool: "place-text", label: "テキストを配置", icon: TitleIconData },
    { tool: "draw-pen", label: "ペンで描画", icon: BrushIconData },
  ];

  const choosePenColor = (color: PenColor): void => {
    setPenColor(color);
    savePenColor(color);
  };
  const chooseStickyColor = (color: StickyColor): void => {
    setStickyColor(color);
    saveStickyColor(color);
  };

  return (
    <div className="canvas-toolbar-shell" role="group" aria-label="キャンバスツール">
      {tool === "place-shape" ? (
        <SubPanel label="図形の種類">
          {SHAPE_OPTIONS.map((option) => (
            <Button
              key={option.kind}
              type="button"
              variant={shapeKind === option.kind ? "action emphasis" : "action"}
              aria-label={option.label}
              aria-pressed={shapeKind === option.kind}
              title={option.label}
              onClick={() => setShapeKind(option.kind)}
            >
              <Icon d={option.icon} />
            </Button>
          ))}
        </SubPanel>
      ) : null}
      {tool === "draw-pen" ? (
        <SubPanel label="ペンの色">
          {PEN_COLOR_OPTIONS.map((color) => (
            <ColorButton
              key={color}
              label={PEN_COLOR_LABEL[color]}
              color={PEN_COLOR_HEX[color]}
              selected={penColor === color}
              onClick={() => choosePenColor(color)}
            />
          ))}
        </SubPanel>
      ) : null}
      {tool === "place-sticky" ? (
        <SubPanel label="付箋の色">
          {STICKY_COLOR_OPTIONS.map((color) => (
            <ColorButton
              key={color}
              label={STICKY_COLOR_LABEL[color]}
              color={STICKY_COLOR_HEX[color]}
              selected={stickyColor === color}
              onClick={() => chooseStickyColor(color)}
            />
          ))}
        </SubPanel>
      ) : null}
      <div role="toolbar" aria-label="キャンバスツールバー" className="canvas-toolbar">
        {/* グリッド吸着の ON/OFF。付箋ボタンの左に配置し、設定はローカルストレージに保存する。 */}
        <BinarySwitch
          aria-label="グリッド吸着の有効/無効"
          className="canvas-grid-snap-switch"
          data-testid="grid-snap-switch"
          onChange={setGridSnapEnabled}
          options={[
            { d: GridOffIconData, value: false },
            { d: GridOnIconData, value: true },
          ]}
          title={gridSnapEnabled ? "グリッド吸着: ON" : "グリッド吸着: OFF"}
          value={gridSnapEnabled}
        />
        {buttons.map((b) => (
          <Button
            key={b.tool}
            type="button"
            aria-label={b.label}
            aria-pressed={tool === b.tool}
            title={b.label}
            onClick={() => setTool(tool === b.tool ? "idle" : b.tool)}
            data-testid={`tool-button-${b.tool}`}
            variant={tool === b.tool ? "action emphasis" : "action"}
          >
            <Icon d={b.icon} />
          </Button>
        ))}
      </div>
    </div>
  );
}

function SubPanel({ children, label }: { readonly children: React.ReactNode; readonly label: string }): JSX.Element {
  return (
    <div className="canvas-toolbar-subpanel" aria-label={label}>
      <span className="canvas-toolbar-subpanel-label">{label}</span>
      <div className="canvas-toolbar-subpanel-actions">{children}</div>
    </div>
  );
}

function ColorButton({
  color,
  label,
  onClick,
  selected,
}: {
  readonly color: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly selected: boolean;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant={selected ? "action emphasis" : "action"}
      aria-label={label}
      aria-pressed={selected}
      title={label}
      onClick={onClick}
      className="canvas-color-button"
      style={{ "--canvas-color-swatch": color } as React.CSSProperties}
    >
      <span className="canvas-color-swatch" aria-hidden="true" />
    </Button>
  );
}

const SHAPE_OPTIONS: readonly {
  readonly kind: ShapeKind;
  readonly label: string;
  readonly icon: string;
}[] = [
  { kind: "rectangle", label: "四角", icon: Crop169IconData },
  { kind: "circle", label: "丸", icon: CircleIconData },
  { kind: "arrow", label: "矢印", icon: ArrowOutwardIconData },
];

const PEN_COLOR_OPTIONS: readonly PenColor[] = ["black", "red", "blue"];
const PEN_COLOR_LABEL: Record<PenColor, string> = {
  black: "黒",
  red: "赤",
  blue: "青",
};

const STICKY_COLOR_OPTIONS: readonly StickyColor[] = ["yellow", "pink", "blue", "green"];
const STICKY_COLOR_LABEL: Record<StickyColor, string> = {
  yellow: "黄",
  pink: "桃",
  blue: "青",
  green: "緑",
};
